// src/components/core/PasswordResetModal.tsx
import React from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import Modal from "./Modal";
import Paragraph from "./typography/Paragraph";
import FormLabel from "./typography/FormLabel";
import InputType from "./form-components/InputType";
import Button from "./form-components/Button";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { languageIdSelector } from "../../redux/slices/website/languageSlice";
import { LanguageTranslation } from "../language-translation/LanguageTranslation";
import { resetPasswordValidationSchemaThoughEmail } from "../../validations/website/resetPasswordValidationSchema";
import { showLoader, hideLoader } from "../../redux/slices/siteLoaderSlice";
import { ToastShow } from "../../redux/slices/toastSlice";
import { pathRoute } from "../../routes/PathRoute";
import { resetPasswordThunk, setPasswordModalOpen, setPasswordSelector } from "../../redux/slices/setPassWordSlice";
import { AppDispatch } from "../../redux/store";
import artGapiLogo from "../../assets/images/artgapiLogo.png";
import { on } from "events";
import { tokenSelector } from "../../redux/slices/userSlice";

interface RegisterFormValues {
  new_password: string;
  confirm_password: string;
}

interface PasswordResetModalProps {
  open: boolean;
  onClose: () => void;
  userId: string | null;
}

const PasswordResetModal: React.FC<PasswordResetModalProps> = ({
  open,
  onClose,
  userId,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const language = useSelector(languageIdSelector);
  const langId = language?.languageId ?? "fr";
   const token = useSelector(tokenSelector);
 
  const { isLoading } = useSelector(setPasswordSelector);

  const initialValues: RegisterFormValues = {
    new_password: "",
    confirm_password: "",
  };

  const handleSubmit = async (values: RegisterFormValues) => {
    dispatch(showLoader());
    
    try {
      if (token && userId) {
        const result = await dispatch(
          resetPasswordThunk({ token, userId, data: values })
        ).unwrap();
      
        if (result.code === 200) {
          dispatch(
            ToastShow({
              message:
                LanguageTranslation({
                  labelName: "web_password_set_success",
                  languageCode: langId,
                }) || "Password set successfully",
              type: "success",
            })
          );
          dispatch(setPasswordModalOpen(false)); // Ensure modal closes
          onClose();
        }
      }
    } catch (error: any) {
      const messageKey = error || "error_reset_password";
      const translatedMessage = LanguageTranslation({
        labelName: messageKey,
        languageCode: langId,
      });
      dispatch(
        ToastShow({
          message: translatedMessage || "Failed to reset password",
          type: "error",
        })
      );
    } finally {
      dispatch(hideLoader());
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      closeOnBackdropClick={false}
      width="w-[90vw] sm:w-[80vw] md:w-[500px] max-w-[500px]"
      modalPadding="p-6 sm:p-8"
      header={false}
      aria-label="Reset Password Modal"
    >
      <div className="flex flex-col items-center gap-6 animate-[fadeInUp_0.5s_ease-out]">
        <img
          src={artGapiLogo}
          alt="Artgapi Logo"
          className="w-[180px] h-[74px] object-contain inline-block"
        />
        <Paragraph className="text-center text-gray-600 font-semibold text-lg sm:text-xl max-w-md">
          {LanguageTranslation({
            labelName: "web_set_new_password_account",
            languageCode: langId,
          }) ||
            "Set the new password for your account so you can continue and access all the features."}
        </Paragraph>
        <Formik
          initialValues={initialValues}
          validationSchema={resetPasswordValidationSchemaThoughEmail(langId)}
          onSubmit={handleSubmit}
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            handleBlur,
            handleSubmit,
            isSubmitting,
          }) => (
            <Form className="w-full">
              <div className="form-group mb-6">
                <FormLabel>
                  {LanguageTranslation({
                    labelName: "web_new_password",
                    languageCode: langId,
                  }) || "New Password"}{" "}
                  <span className="text-site-red">*</span>
                </FormLabel>
                <InputType
                  placeholder={
                    LanguageTranslation({
                      labelName: "web_new_password_placeholder",
                      languageCode: langId,
                    }) || "Enter new password"
                  }
                  type="password"
                  name="new_password"
                  onKeyDown={(e: any) => {
                    if (e.key === " ") e.preventDefault();
                  }}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.new_password}
                />
              
              </div>
              <div className="form-group mb-[30px]">
                <FormLabel>
                  {LanguageTranslation({
                    labelName: "web_confirm_password",
                    languageCode: langId,
                  }) || "Confirm Password"}{" "}
                  <span className="text-site-red">*</span>
                </FormLabel>
                <InputType
                  placeholder={
                    LanguageTranslation({
                      labelName: "web_enter_confirm_password",
                      languageCode: langId,
                    }) || "Enter confirm password"
                  }
                  type="password"
                  name="confirm_password"
                  onKeyDown={(e: any) => {
                    if (e.key === " ") e.preventDefault();
                  }}
                  onPaste={(e: any) => {
                    e.preventDefault();
                  }}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.confirm_password}
                />
               
              </div>
              <Button
                primary
                type="submit"
                className="w-full px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition-colors duration-200 text-base shadow-md"
                disabled={isSubmitting || isLoading}
              >
                {LanguageTranslation({
                  labelName: "web_submit",
                  languageCode: langId,
                }) || "Submit"}
              </Button>
            </Form>
          )}
        </Formik>
      </div>
    </Modal>
  );
};

export default PasswordResetModal;